import { useAccordionItemIndex } from "@/components/builder/accordion/accordion";
import PlusMinusTransition from "@/components/icons/plus-minus-transition";
import { zeroPrefixedNumber } from "@/util/zero-prefix-number";
import { useWindowHash } from "@/hooks/use-window-hash";
import getBuilderLocalizedValue from "@/i18n/get-builder-localized-value";
import { useLocale } from "@/libs/base/components/locale-provider";
import { disablePropagation } from "@/libs/base/utils/events";
import { cn } from "@/libs/base/utils/layout";
import { withChildren } from "@builder.io/react";
import { ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import React from "react";
import { AccordionItemDescriptorType, useAccordion } from "./context";

const NAVBAR_HEIGHT = 50; // px

export interface AccordionItemProps extends AccordionItemDescriptorType {
  children: ReactNode;
}

export const AccordionItem = withChildren(function AccordionItem({ children, title, imageURL }: AccordionItemProps) {
  const ctx = useAccordion();
  const locale = useLocale();
  const index = useAccordionItemIndex();

  const descriptor: AccordionItemDescriptorType = useMemo(
    () =>
      ({
        title: getBuilderLocalizedValue(title, locale),
        imageURL,
      }) satisfies AccordionItemDescriptorType,
    [title, imageURL, locale],
  );

  const windowHash = useWindowHash();
  const [isSectionNearTop, setIsSectionNearTop] = useState(false);
  const isOpened = ctx.opened === descriptor;
  const isSticky = isOpened && isSectionNearTop;

  const mainRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<NodeJS.Timeout>();
  const headingRef = useRef<HTMLButtonElement>(null);

  // Since the accordion can have multiple ids, we need to iterate over the children to understand
  // if the current accordion item has a children with an id that's in the url.
  // Since we can't use a ref in an effect, this needs to be recreated according to the react docs.
  // It's somewhat ugly but this is the closest we could go
  // https://stackoverflow.com/a/59605067
  const [childrenNode, setChildrenNode] = useState<Element | null>(null);

  const updateHeaderStickyness = useCallback(() => {
    const main = mainRef.current;
    if (!main) return;
    setIsSectionNearTop(main.getBoundingClientRect().top < NAVBAR_HEIGHT + 10); /* px */
  }, [isOpened, mainRef]);

  // Clear the timeout that is set on the click. Probably not needed as there won't be elements to scroll to, but just in case...
  // https://stackoverflow.com/a/77552201
  useEffect(() => {
    return () => {
      if (scrollRef.current != null) {
        window.clearTimeout(scrollRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const fragment = windowHash || window.location.hash;
    if (!fragment) {
      return;
    }

    const anchorElement = childrenNode?.querySelector(fragment);
    if (anchorElement) {
      ctx.setOpened(descriptor);
      setTimeout(() => anchorElement.scrollIntoView({ behavior: "smooth" }), 30);
    }
  }, [windowHash, childrenNode]);

  useEffect(() => {
    window.addEventListener("scroll", updateHeaderStickyness);

    return () => {
      window.removeEventListener("scroll", updateHeaderStickyness);
    };
  }, [updateHeaderStickyness]);

  useLayoutEffect(() => {
    updateHeaderStickyness();
  }, [isOpened]);

  return (
    <div className="w-full border-t border-black last-of-type:border-b" ref={mainRef}>
      <button
        type="button"
        className={cn(
          "group top-0 z-10 grid w-full origin-top scroll-mt-[36px] grid-cols-[32px,1fr] items-end gap-2.5 bg-white px-4 py-3 text-left active:outline-none sm:grid-cols-[96px,1fr] sm:px-9 sm:py-4",
          isOpened && "sticky top-[50px] border-b border-black",
          isSticky && "items-center py-1 pr-2",
        )}
        onMouseEnter={() => ctx.setHovered(descriptor)}
        onMouseLeave={() => ctx.setHovered(null)}
        onPointerEnter={(e) => {
          // stop random scrolling up because mobile browsers try to scroll to the focused element
          headingRef.current?.releasePointerCapture(e.pointerId);
        }}
        onClick={disablePropagation(() => {
          isOpened ? ctx.setOpened(null) : ctx.setOpened(descriptor);
          if (mainRef.current) {
            // It needs the timeout as the content is not immediatelly loaded ... Javascript shananingans
            scrollRef.current = setTimeout(() => {
              const elementPosition = mainRef.current?.getBoundingClientRect().top as number;
              const offsetPosition = elementPosition + window.scrollY - NAVBAR_HEIGHT;
              window.scrollTo({ top: offsetPosition });
            }, 30);
          }
        })}
      >
        {/* FIXME: Need to position this better when the title wraps around. it should stay in the first line */}
        <span className="font-light">{zeroPrefixedNumber(index)}</span>

        <div className="flex w-full select-none items-center justify-between gap-2">
          <h2
            className={cn(
              "block origin-left text-display font-light transition-all group-hover:-skew-x-12 group-hover:text-stroke",
              isSticky && "text-2xl leading-7 group-hover:skew-x-0 group-hover:text-stroke-none",
            )}
          >
            {descriptor.title}
          </h2>

          <PlusMinusTransition
            showMode={isOpened ? "minus" : "plus"}
            className={cn(
              "pointer-events-none h-10 w-10 text-black transition-all sm:h-16 sm:w-16",
              isSticky && "h-6 w-6 sm:h-6 sm:w-6",
            )}
            iconClassName="h-full w-full"
          />
        </div>
      </button>

      <div ref={setChildrenNode} className={isOpened ? "" : "hidden"}>
        {children}
      </div>
    </div>
  );
});
